import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Image } from '@/components/common/image';
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { TruncatedText } from "../common/truncated-text";
import { CopyClipboard } from "../common/copy-clipboard";
import { Spinner } from "../ui/modal";
import { TextButton } from "../common/text-button";
import { DeleteIcon } from "../agent-dashboard/assets";
import { Video } from "../common/video";
import VideoController from "../video-controller";
import { DisabledIcon, EnabledIcon } from "./assets";

interface TemplateDetailModalProps {
  /** 1 短视频; 4 图文; 5 文章*/
  type: number;
  templateId: string;
  templateName: string;
  loading?: boolean;
  open: boolean;
  id: number;
  deleteLoading?: boolean;
  coverUrl: string;
  coverVideo?: string;
  status?: number;
  onToggleStatus?: (id: number) => void;
  onDelete?: (id: number) => void;
  onOpenChange: (open: boolean) => void;
}

export interface TemplateDetailAction {
  onClose?: () => void;
}

/** 模板详情弹窗 */
export const TemplateDetailModal = forwardRef<TemplateDetailAction, TemplateDetailModalProps>(({
  open,
  id,
  type,
  coverUrl,
  coverVideo,
  templateName,
  templateId,
  loading = false,
  deleteLoading = false,
  status,
  onDelete,
  onOpenChange,
  onToggleStatus,
}, ref) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [load, setLoad] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useImperativeHandle(ref, () => ({
    onClose: () => {
      onClose();
    },
  }));

  const handleDelete = async () => {
    onDelete?.(id);
  }

  const handleToggleStatus = () => {
    onToggleStatus?.(id);
  }

  const onClose = () => {
    onOpenChange(false);
  }

  // 自动播放视频
  useEffect(() => {
    if (type !== 1) return;
    const video = videoRef.current;
    if (!video || !open) return;

    const handleCanPlayThrough = async () => {
      try {
        await video.play();
      } catch (error) {
        console.warn("Auto-play failed:", error);
      }
    };

    video.addEventListener("canplaythrough", handleCanPlayThrough);

    if (video.readyState >= 3) {
      handleCanPlayThrough();
    }

    return () => {
      video.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, [open, coverVideo]);

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (open) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={(value) => onOpenChange(value)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[840px] h-[750px] max-w-[1055px] max-h-[750px] rounded-2xl shadow-lg z-50 flex overflow-hidden">
          <div
            className={cn(
              "group relative flex-1 bg-black flex flex-col items-center justify-center overflow-hidden", // 添加 overflow-hidden 防止溢出
              loading && "bg-gray-200"
            )}
          >
            {!loading && (
              <div className="absolute inset-0 flex items-center justify-center border-r border-[rgba(213,215,218,1)]">
                {type === 1 && coverVideo ? (
                  <Video
                    ref={videoRef}
                    src={coverVideo}
                    className="h-full w-full object-cover"
                    poster={coverUrl}
                    autoPlay
                    preload="auto"
                  />
                ) : (
                  <Image
                    src={coverUrl}
                    alt={templateName}
                    // 确保图片撑满
                    className="h-full w-full object-contain"
                  />
                )}
                {/* 加载状态层 */}
                {coverVideo && load && (
                  <div className="absolute inset-0 flex justify-center items-center bg-black/20">
                    <Spinner className="text-white" size={32} />
                  </div>
                )}
                {/* 控制条 */}
                {coverVideo && (
                  <div className="opacity-0 group-hover:opacity-100 absolute bottom-0 left-0 right-0 transition-opacity duration-250 z-10">
                    <VideoController videoRef={videoRef} onLoad={setLoad} />
                  </div>
                )}
              </div>
            )}
          </div>
          <div ref={titleRef} tabIndex={-1} className="hidden"></div>
          {/* 右侧信息面板 */}
          <div className="w-[26.25rem] bg-i-white flex flex-col gap-4 p-6">
            <div className="flex justify-between items-center">
              <span className="text-xl font-semibold text-[rgba(16,24,40,1)]">
                {t("agent_dashboard.company_asset.detail_modal.title")}
              </span>
              <button
                className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
                tabIndex={-1}
                onClick={() => {
                  onClose?.();
                }}
              >
                <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
              </button>
            </div>
            {/* 模板ID */}
            <div className={cn(
              "space-y-2",
            )}>
              <div className="flex items-center gap-2">
                <span className="text-sm text-i-sub">
                  {t("agent_dashboard.company_asset.detail_modal.template_id")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <TruncatedText
                  text={templateId}
                  className="max-w-[70%] text-sm text-[rgba(16,24,40,1)] text-medium"
                />
                <CopyClipboard text={templateId} iconClassName="size-4" />
              </div>
            </div>
            {/* 模板名称 */}
            <div className={cn(
              "space-y-2",
              type !== 1 && "flex-1"
            )}>
              <div className="flex items-center gap-2">
                <span className="text-sm text-i-sub">
                  {t("agent_dashboard.company_asset.detail_modal.template_name")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <TruncatedText
                  text={templateName}
                  className="max-w-[70%] text-sm text-[rgba(16,24,40,1)] text-medium"
                />
              </div>
            </div>
            {/* 模板封面 */}
            {type === 1 && <div className="space-y-2  flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm text-i-sub">
                  {t("agent_dashboard.company_asset.detail_modal.template_cover")}
                </span>
              </div>
              <div className="w-full flex items-center gap-3">
                <div className="w-[6.5625rem] h-[8.75rem] overflow-hidden flex-shrink-0">
                  <Image src={coverUrl} className="size-full rounded-[12px] object-cover" />
                </div>
              </div>
            </div>}
            {/* 操作按钮 */}
            <div className={cn(
              "flex gap-3 justify-end",
            )}>
              <TextButton
                icon={status === 1 ? <DisabledIcon className="w-4 h-4 text-[#344054d8]" /> : <EnabledIcon className="w-4 h-4 text-[#344054d8]" />}
                label={status === 1 ? t("agent_dashboard.company_asset.disable") : t("agent_dashboard.company_asset.enable")}
                className={cn(
                  "h-10 w-[11.25rem] bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium text-sm !text-[rgba(52,64,84,1)]"
                )}
                onClick={handleToggleStatus}
              />
              <TextButton
                icon={<DeleteIcon className="w-5 h-5 text-[rgba(52,64,84,1)]" />}
                label={t('agent_dashboard.company_asset.delete')}
                className='h-10 w-[11.25rem] bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium text-sm !text-[rgba(52,64,84,1)]'
                onClick={handleDelete}
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
})